/**
 * What this user has bought, and what they have left to spend — the source of
 * truth for every gate in the UI.
 *
 * Fetched once after auth from `GET /billing/me`, which returns what is held
 * (with each product's plan), the full catalog, the credit balance, the active
 * price book and the plan table. The catalog and the prices come from the
 * SERVER rather than constants here so that adding a product to
 * `jcseo/types.py:Product`, or retuning a price, shows up without a frontend
 * release, and the two can never disagree about what is on offer.
 *
 * The balance is kept current from the `credits` websocket event the server
 * sends after every charge and grant, so the boardroom's chip ticks down as a
 * dig runs without polling.
 *
 * THIS IS NOT ACCESS CONTROL. Hiding a nav entry stops someone stumbling into a
 * product they have not bought; it does not stop them typing the URL. The
 * backend's `require_product` / `require_credits` dependencies are what
 * enforce, returning 402, and the two are allowed to disagree briefly — a
 * revoked entitlement takes effect on the API immediately and in this cache on
 * the next load.
 *
 * FAILS CLOSED-BUT-QUIET: a failed fetch yields no products and no credits
 * rather than throwing, so a backend blip empties the nav instead of
 * white-screening the app. `loading` is exposed so callers can avoid rendering
 * a flash of "you own nothing" before the answer arrives.
 */
import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { EMPTY_BILLING, applyCreditsEvent } from '../billing/credits';
import type {
  BillingInfo,
  CreditsBalance,
  PlanInfo,
  PlanKey,
  PriceBook,
  Product,
  ProductCatalogEntry,
  ProductUsage,
  Sellable,
} from '../types';

interface EntitlementContextType {
  /** Products that currently GRANT access (active or trialing). */
  held: Product[];
  /** Every product that exists, whether held or not. */
  available: Product[];
  /** Rows including lapsed ones — the launcher shows "lapsed", not "never had". */
  entitlements: BillingInfo['products'];
  has: (product: Product) => boolean;
  hasAll: (...products: Product[]) => boolean;
  /** The plan key this user holds a product on ("free" when unheld). */
  planOf: (product: Product) => PlanKey;
  /** The plan table, keyed by plan. Empty until the fetch lands. */
  plans: Record<string, PlanInfo>;
  /** Availability of each VISIBLE product; absent = not yours to see. */
  catalogOf: (product: Product) => ProductCatalogEntry | null;
  credits: CreditsBalance;
  priceBook: PriceBook;
  /** This period's usage by product: the receipt beneath the credit figure. */
  usage: Record<string, ProductUsage>;
  /** What can be bought right now, from the server's Stripe price rows. */
  sellables: Sellable[];
  /** Has ever been a Stripe customer — gates the "Manage billing" door. */
  hasStripeCustomer: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

const EntitlementContext = createContext<EntitlementContextType | null>(null);

export function EntitlementProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { subscribe } = useWebSocket();
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setInfo(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setInfo(await api.getBilling());
    } catch {
      // A blip empties the nav; it must not take the app down with it.
      setInfo(EMPTY_BILLING);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  // The balance moves on every charge; fold the event in rather than refetch.
  useEffect(
    () =>
      subscribe((event) => {
        if (event.event_type !== 'credits') return;
        setInfo((prev) =>
          prev ? { ...prev, credits: applyCreditsEvent(prev.credits, event) } : prev,
        );
      }),
    [subscribe],
  );

  const products = info?.products ?? [];
  const held = products.filter((e) => e.active).map((e) => e.product);

  const value: EntitlementContextType = {
    held,
    available: info?.available ?? [],
    entitlements: products,
    has: (product) => held.includes(product),
    hasAll: (...wanted) => wanted.every((p) => held.includes(p)),
    planOf: (product) => products.find((e) => e.active && e.product === product)?.plan ?? 'free',
    plans: info?.plans ?? {},
    catalogOf: (product) => info?.catalog?.[product] ?? null,
    credits: info?.credits ?? EMPTY_BILLING.credits,
    priceBook: info?.price_book ?? EMPTY_BILLING.price_book,
    usage: info?.usage ?? {},
    sellables: info?.sellables ?? [],
    hasStripeCustomer: info?.has_stripe_customer ?? false,
    loading,
    refresh: load,
  };

  return (
    <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>
  );
}

export function useEntitlements(): EntitlementContextType {
  const ctx = useContext(EntitlementContext);
  if (!ctx) {
    throw new Error('useEntitlements must be used within an EntitlementProvider');
  }
  return ctx;
}
